import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Zap, Wrench, Thermometer, Lightbulb, CheckCircle, Clock, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from '../components/modals/QuoteModal';

const DistributionPage = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const productCategories = [
    {
      icon: Zap,
      name: 'Electrical Systems',
      description: 'Cables, switches, panels, lighting fixtures, and smart electrical solutions',
      products: ['Power Cables', 'Control Panels', 'LED Lighting', 'Smart Switches', 'Distribution Boards']
    },
    {
      icon: Wrench,
      name: 'Plumbing Solutions', 
      description: 'Pipes, fittings, valves, pumps, and water management systems',
      products: ['PVC/Copper Pipes', 'Valve Systems', 'Water Pumps', 'Bathroom Fixtures', 'Drainage Systems']
    },
    {
      icon: Thermometer,
      name: 'HVAC Systems',
      description: 'Heating, ventilation, air conditioning, and climate control equipment',
      products: ['Air Conditioning', 'Ventilation Ducts', 'Heat Pumps', 'Thermostats', 'Air Filters']
    },
    {
      icon: Lightbulb,
      name: 'Smart Building Tech',
      description: 'IoT sensors, automation systems, and intelligent building solutions',
      products: ['IoT Sensors', 'Building Automation', 'Security Systems', 'Energy Management', 'Smart Controls']
    }
  ];

  const services = [
    {
      title: 'Same-Day Delivery',
      description: 'Urgent orders delivered within Amsterdam metropolitan area',
      icon: Clock
    },
    {
      title: 'Technical Support',
      description: '24/7 expert assistance for installation and troubleshooting',
      icon: CheckCircle
    },
    {
      title: 'Site Delivery',
      description: 'Direct delivery to construction sites with scheduling coordination',
      icon: MapPin
    }
  ];

  const partners = [
    { name: 'Schneider Electric', specialization: 'Electrical Solutions' },
    { name: 'Grohe', specialization: 'Plumbing & Fixtures' },
    { name: 'Daikin', specialization: 'HVAC Systems' },
    { name: 'Philips Lighting', specialization: 'LED & Smart Lighting' },
    { name: 'Honeywell', specialization: 'Building Automation' },
    { name: 'Grundfos', specialization: 'Pump Solutions' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:bg-gray-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-800">Distribution Services</Badge>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Electrical & Plumbing
                <span className="block font-medium">Distribution Excellence</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Complete electrical and plumbing solutions from leading European manufacturers. 
                Fast delivery, technical support, and inventory management for your projects.
              </p>
              <div className="flex space-x-4">
                <Button 
                  onClick={() => setIsQuoteModalOpen(true)}
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Zap size={20} className="mr-2" />
                  Request Quote
                </Button>
                <Button size="lg" variant="outline">
                  Browse Catalog
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=2069" 
                alt="Electrical Distribution"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Product Categories
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {productCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} className="text-blue-600" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900">{category.name}</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{category.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Key Products:</p>
                      <div className="flex flex-wrap gap-2">
                        {category.products.map((product) => (
                          <Badge key={product} variant="outline" className="text-xs">
                            {product}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Distribution Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Trusted Brand Partners
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {partners.map((partner, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h4 className="font-medium text-gray-900 mb-2">{partner.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    {partner.specialization}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-light mb-2">10,000+</div>
              <div className="text-gray-300">Products in Stock</div>
            </div>
            <div>
              <div className="text-4xl font-light mb-2">48h</div>
              <div className="text-gray-300">Average Delivery</div>
            </div>
            <div>
              <div className="text-4xl font-light mb-2">24/7</div>
              <div className="text-gray-300">Technical Support</div>
            </div>
            <div>
              <div className="text-4xl font-light mb-2">500+</div>
              <div className="text-gray-300">Active Projects</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Need Electrical or Plumbing Solutions?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our distribution experts are ready to help with your project requirements.
          </p>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-gray-900 hover:bg-gray-800"
          >
            Get Distribution Quote
          </Button>
        </div>
      </section>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </div>
  );
};

export default DistributionPage;